import { useContext, useEffect, useState } from "react";
import "../Announcement/Announcement.scss";
import TextArea from "../../../components/Fields/TextArea";
import {
  styles,
  labelStyles,
  textAreaStyles,
} from "../Announcement/AnnouncementStyles";
import { toast } from "react-toastify";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { confirmDialog } from "primereact/confirmdialog";
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { AppContext } from "../../../context/AppContext";
import api from "../../../api.js";
import Loader from "../../../components/Loader/Loader.jsx";

const News = () => {
  const { backendUrl } = useContext(AppContext);
  const [allNews, setAllNews] = useState([]);
  const [newsText, setNewsText] = useState("");
  const [visible, setVisible] = useState(false);
  const [selectedNews, setSelectedNews] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [updatedNews, setUpdatedNews] = useState({});
  const [validationError, setValidationError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scrollHeight, setScrollHeight] = useState("425px");
  let errorShown = false;

  useEffect(() => {
    const handleResize = () => {
      const screenWidth = window.innerHeight;
      if (screenWidth >= 900) {
        setScrollHeight("650px");
      } else {
        setScrollHeight("440px");
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  async function fetchAllNews() {
    setLoading(true);
    try {
      const { data } = await api.get(`${backendUrl}/api/auth/getAllNews`);
      if (data.success) {
        setAllNews(data.news || []);
      } else {
        toast.error(data.message);
      }
      errorShown = false;
    } catch (error) {
      if (errorShown) return;
      errorShown = true;
      if (!navigator.onLine) {
        toast.error("🚫 No internet connection. Please check your network.");
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.request && !error.response) {
        toast.error(
          "⚠️ Network issue or server is unreachable. Try again later."
        );
      } else if (error.response && error.response.status === 400) {
        toast.error(error.response.data.message);
      } else if (error.response && error.response.status === 404) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Something went wrong.");
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchAllNews();
  }, []);

  const validateInput = (text) => {
    const trimmedText = text.trim();
    if (!trimmedText) {
      return "News is required";
    }
    return null;
  };

  async function createNews(e) {
    e.preventDefault();
    const error = validateInput(newsText);
    if (error) {
      setValidationError(error);
      return;
    }
    setIsSubmitting(true);
    try {
      const { data } = await api.post(`${backendUrl}/api/auth/createNews`, {
        news: newsText,
      });
      document.activeElement?.blur();
      if (data.success) {
        toast.success(data.message);
        setNewsText("");
        setVisible(false);
        fetchAllNews();
        setValidationError(null);
      }
      errorShown = false;
    } catch (error) {
      if (errorShown) return;
      errorShown = true;
      if (!navigator.onLine) {
        toast.error("🚫 No internet connection. Please check your network.");
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.request && !error.response) {
        toast.error(
          "⚠️ Network issue or server is unreachable. Try again later."
        );
      } else {
        toast.error("Something went wrong.");
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  const handleEditClick = (rowData) => {
    setSelectedNews(rowData);
    setUpdatedNews(rowData);
    setVisible(true);
  };

  const updateNews = async (e) => {
    e.preventDefault();
    const error = validateInput(updatedNews.news);
    if (error) {
      setValidationError(error);
      return;
    }
    setIsSubmitting(true);
    if (!updatedNews.news || updatedNews.news.trim() === "") {
      toast.error("News content cannot be empty");
      return;
    }
    try {
      const { data } = await api.put(
        `${backendUrl}/api/auth/updateNews/${selectedNews.id}`,
        updatedNews
      );
      if (data.success) {
        toast.success(data.message);
        fetchAllNews();
        setVisible(false);
        setSelectedNews(null);
        setUpdatedNews({});
        setValidationError(null);
      } else {
        toast.error(data.message);
      }
      errorShown = false;
    } catch (error) {
      if (errorShown) return;
      errorShown = true;
      if (!navigator.onLine) {
        toast.error("🚫 No internet connection. Please check your network.");
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.request && !error.response) {
        toast.error(
          "⚠️ Network issue or server is unreachable. Try again later."
        );
      } else {
        toast.error("Something went wrong.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const showConfirmDialog = (rowData) => {
    confirmDialog({
      message: (
        <div className="custom-dialog-content">
          <div className="iconParent">
            <i className="pi pi-times"></i>
          </div>
          <div className="contentParent">
            <h2>Are you sure?</h2>
            <div>
              <p>Do you really want to delete this news ? </p>
              <p> This process cannot be undone.</p>
            </div>
          </div>
        </div>
      ),
      header: "",
      icon: null,
      acceptLabel: "Delete",
      rejectLabel: "Cancel",
      acceptClassName: "p-button-danger custom-delete-btn",
      rejectClassName: "p-button-secondary custom-cancel-btn",
      accept: () => {
        handleDeleteClick(rowData);
      },
    });
  };

  async function handleDeleteClick(rowData) {
    setIsSubmitting(true);
    try {
      const { data } = await api.delete(
        `${backendUrl}/api/auth/deleteNews/${rowData.id}`
      );
      if (data.success) {
        toast.success(data.message);
        fetchAllNews();
      }
      errorShown = false;
    } catch (error) {
      if (errorShown) return;
      errorShown = true;
      if (!navigator.onLine) {
        toast.error("🚫 No internet connection. Please check your network.");
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.request && !error.response) {
        toast.error(
          "⚠️ Network issue or server is unreachable. Try again later."
        );
      } else {
        toast.error("Something went wrong.");
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  const toggleStatus = async (rowData) => {
    const id = rowData.id;
    const newStatus = !rowData.isActive;
    setIsSubmitting(true);
    try {
      const response = await api.patch(
        `${backendUrl}/api/auth/toggleNewsStatus/${id}`,
        { isActive: newStatus }
      );
      const updatedNews = response.data.data;
      const updatedList = allNews.map((news) =>
        news.id === id ? updatedNews : newsText
      );
      if (response.data.success) {
        toast.success(response.data.message);
      }
      fetchAllNews();
    } catch (error) {
      if (errorShown) return;
      errorShown = true;
      if (!navigator.onLine) {
        toast.error("🚫 No internet connection. Please check your network.");
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.request && !error.response) {
        toast.error(
          "⚠️ Network issue or server is unreachable. Try again later."
        );
      } else if (error.response && error.response.status === 404) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Something went wrong.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleRecentStatus = async (rowData) => {
    const id = rowData.id;
    const newStatus = !rowData.recent;
    setIsSubmitting(true);
    try {
      const response = await api.patch(
        `${backendUrl}/api/auth/toggleNewsRecentStatus/${id}`,
        { recent: newStatus }
      );
      const updatedNews = response.data.data;
      const updatedList = allNews.map((news) =>
        news.id === id ? updatedNews : news
      );
      if (response.data.success) {
        toast.success(response.data.message);
      }
      fetchAllNews();
      errorShown = false;
    } catch (error) {
      if (errorShown) return;
      errorShown = true;
      if (!navigator.onLine) {
        toast.error("🚫 No internet connection. Please check your network.");
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.request && !error.response) {
        toast.error(
          "⚠️ Network issue or server is unreachable. Try again later."
        );
      } else if (error.response && error.response.status === 404) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Something went wrong.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="adminNewsMainContainer">
      <Loader visible={isSubmitting} />
      <div className="headerParent">
        <h2>Welcome to Admin News Page</h2>
      </div>
      <Dialog
        header={selectedNews ? "Edit News" : "Post News"}
        visible={visible}
        style={{ width: "40vw" }}
        onHide={() => {
          setVisible(false);
          setSelectedNews(null);
          setUpdatedNews({});
          setNewsText("");
          setValidationError(null);
        }}
        draggable={false}
        resizable={false}
        modal
        className="customAnnouncementDialog"
      >
        <form onSubmit={selectedNews ? updateNews : createNews}>
          <TextArea
            styles={styles}
            inputStyles={textAreaStyles}
            labelStyles={labelStyles}
            customLabelName="News"
            required={!selectedNews}
            placeholder="Type the News"
            value={selectedNews ? updatedNews.news : newsText}
            handleChange={(e) => {
              setValidationError(null);
              if (selectedNews) {
                setUpdatedNews({ ...updatedNews, news: e.target.value });
              } else {
                setNewsText(e.target.value);
              }
            }}
          />

          {/* Validation error message */}
          {validationError && (
            <div className="validation-error">
              <i className="pi pi-exclamation-triangle"></i>
              {validationError}
            </div>
          )}

          <div className="submitBtnParent">
            <button
              type="submit"
              className="submitButton"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <i className="pi pi-spinner pi-spin"></i> Processing...
                </>
              ) : selectedNews ? (
                "Update News"
              ) : (
                "Create News"
              )}
            </button>
          </div>
        </form>
      </Dialog>

      <div className="uploadBtnsParent">
        <Button
          label="Create News"
          severity="primary"
          raised
          className="uploadBtn"
          onClick={() => setVisible(true)}
        />
      </div>
      <article className="tableWrapper">
        <div className="scrollableTableContainer">
          <DataTable
            value={allNews}
            showGridlines
            stripedRows
            className="table"
            emptyMessage="No news available"
            paginator
            rows={5}
            rowsPerPageOptions={[5, 10, 20]}
            scrollable
            scrollHeight={scrollHeight}
            loading={loading}
          >
            <Column
              header="S.No"
              body={(_, options) => options.rowIndex + 1}
              className="tableData"
              headerStyle={{ whiteSpace: "nowrap" }}
              bodyStyle={{ whiteSpace: "nowrap" }}
            />
            <Column
              field="news"
              header="News"
              className="tableData"
              style={{ minWidth: "375px", maxWidth: "100%" }}
            />
            <Column
              field="createdAt"
              header="Created At"
              body={(rowData) =>
                new Date(rowData.createdAt).toLocaleDateString()
              }
              className="tableData"
              headerStyle={{ whiteSpace: "nowrap" }}
              bodyStyle={{ whiteSpace: "nowrap" }}
            />

            <Column
              field="updatedAt"
              header="Updated At"
              body={(rowData) =>
                new Date(rowData.updatedAt).toLocaleDateString()
              }
              className="tableData"
              headerStyle={{ whiteSpace: "nowrap" }}
              bodyStyle={{ whiteSpace: "nowrap" }}
              style={{ width: "auto", minWidth: "150px" }}
            />
            <Column
              field="recent"
              header="Recent"
              className="tableData"
              headerStyle={{ whiteSpace: "nowrap" }}
              bodyStyle={{ whiteSpace: "nowrap" }}
              body={(rowData) => (
                <div className="statusBtnParent">
                  <Button
                    label={rowData.recent ? "Yes" : "No"}
                    severity={rowData.recent ? "success" : "danger"}
                    className="statusBtn"
                    onClick={() => toggleRecentStatus(rowData)}
                  />
                </div>
              )}
            />
            <Column
              field="status"
              header="Status"
              headerStyle={{ whiteSpace: "nowrap" }}
              bodyStyle={{ whiteSpace: "nowrap" }}
              className="tableData"
              body={(rowData) => (
                <div className="statusBtnParent">
                  <Button
                    label={rowData.isActive ? "Active" : "Deactive"}
                    severity={rowData.isActive ? "success" : "danger"}
                    className="statusBtn"
                    onClick={() => toggleStatus(rowData)}
                  />
                </div>
              )}
            />
            <Column
              header="Action"
              headerStyle={{ whiteSpace: "nowrap" }}
              bodyStyle={{ whiteSpace: "nowrap" }}
              body={(rowData) => (
                <div className="action-buttons">
                  <Button
                    label="Edit"
                    severity="success"
                    className="actionBtns"
                    onClick={() => handleEditClick(rowData)}
                  />
                  <Button
                    label="Delete"
                    severity="danger"
                    className="actionBtns"
                    onClick={() => showConfirmDialog(rowData)}
                  />
                </div>
              )}
              className="tableData"
            />
          </DataTable>
        </div>
      </article>
    </section>
  );
};

export default News;
